// Sample domain (Sampler port) — shared types, constants, formatters.
// Faithful 1:1 of sampler/src/utils/debounce.js + the API envelope shapes.

import { API_BASE } from '../../lib/api'

export type SampleStatus =
  | 'draft'
  | 'ready_for_factory'
  | 'confirmed'
  | 'sample_ready'
  | 'shipped'
  | 'received'

export type Sample = {
  id: string
  pipeline_id: string
  pipeline_number: number
  title: string | null
  description: string | null
  etsy_url: string | null
  status: SampleStatus
  locked: boolean
  priority: 'urgent' | 'normal' | 'low'
  deleted_at: number | null
  expected_completion_at: number | null
  sample_ready_at: number | null
  shipped_at: number | null
  shipping_carrier: string | null
  tracking_number: string | null
  tracking_url: string | null
  shipping_note: string | null
  received_at: number | null
  adopted_master_catalog_id: string | null
  adopted_sku_id: string | null
  adopted_at: number | null
  created_at: number | null
  updated_at: number | null
  // List-only computed fields:
  comment_count?: number
  last_comment_at?: number | null
  reference_count?: number
  photo_count?: number
  thumbnail?: string | null
  unread_count?: number
  producer_org_id?: string | null
  producer_name?: string | null
  // List spec columns (derived from the brief specs server-side):
  material?: string
  size?: string
  color?: string
}

// Fixed V1 material options (single-select), surfaced as a dropdown at the top
// of the Specs editor. Admin-configurable later.
export const MATERIAL_OPTIONS = [
  'Buffalo Avanza',
  'Buffalo Nevada',
  'Cow Avanza',
  'Cow Nevada',
  'Cow Nubuck',
] as const

export type SampleReference = {
  id: string
  url: string | null
  title: string | null
  description: string | null
  price: string | null
  seller: string | null
  photos: Array<{ url: string } | string>
  tags: string[]
  materials: string[]
}

export type SampleComment = {
  id: string
  actor: string
  role: string
  body: string
  created_at: number | null
  user_id: string | null
  edited: boolean
  updated_at: number | null
}

export type SampleActivity = {
  id: string
  actor: string | null
  role: string | null
  kind: string
  detail: Record<string, unknown>
  created_at: number | null
}

export type BriefSpec = { type: string; label?: string; value?: string }
type BriefPhoto = { url: string; caption?: string }
export type Brief = {
  product_name: string
  photos: BriefPhoto[]
  specs: BriefSpec[]
  notes: string
  // Per-sample price entered by the assigned factory (its quote).
  price?: string | null
  // Buyer's target price (factory is read-only).
  target_price?: string | null
  // Production notes written by the assigned factory (buyer is read-only).
  factory_notes?: string | null
}

export const PHASES: Array<{ key: SampleStatus; label: string }> = [
  { key: 'draft', label: 'Draft' },
  { key: 'ready_for_factory', label: 'Ready for Factory' },
  { key: 'confirmed', label: 'In Production' },
  { key: 'sample_ready', label: 'Sample Ready' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'received', label: 'Received' },
]

const PHASE_FALLBACK: Record<string, SampleStatus> = {
  drafting: 'draft',
  reviewing: 'ready_for_factory',
  at_workshop: 'ready_for_factory',
  sample: 'ready_for_factory',
  product_ready: 'confirmed',
}

export function normalizePhase(key: string | null | undefined): SampleStatus {
  if (!key) return 'draft'
  if (PHASES.some((p) => p.key === key)) return key as SampleStatus
  return PHASE_FALLBACK[key] || 'draft'
}

export function phaseIndex(key: string | null | undefined): number {
  const norm = normalizePhase(key)
  const i = PHASES.findIndex((p) => p.key === norm)
  return i < 0 ? 0 : i
}

export const PRIORITIES = [
  { key: 'urgent', label: 'Urgent', chip: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500' },
  { key: 'normal', label: 'Normal', chip: 'bg-slate-100 appearance-dark:bg-[var(--color-surface-muted)] text-slate-700 appearance-dark:text-[var(--color-text)] border-slate-200 appearance-dark:border-[var(--color-border)]', dot: 'bg-slate-400' },
  { key: 'low', label: 'Low', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
] as const

export function priorityMeta(key: string | null | undefined) {
  return PRIORITIES.find((p) => p.key === (key || 'normal')) || PRIORITIES[1]
}

export const STATUS_STYLES: Record<SampleStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  ready_for_factory: 'bg-blue-100 text-blue-800',
  confirmed: 'bg-emerald-100 text-emerald-800',
  sample_ready: 'bg-violet-100 text-violet-800',
  shipped: 'bg-cyan-100 text-cyan-800',
  received: 'bg-teal-100 text-teal-800',
}

export function formatSampleCode(n: number | null | undefined): string {
  if (n === null || n === undefined) return ''
  const num = Number(n)
  if (!Number.isFinite(num)) return ''
  return 'SS' + String(num).padStart(2, '0')
}

export function relTime(ts: number | null | undefined): string {
  if (!ts) return '—'
  const diff = Date.now() / 1000 - ts
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`
  return new Date(ts * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

export function fmtDate(ts: number | null | undefined): string {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function fmtTime(ts: number | null | undefined): string {
  if (!ts) return ''
  return new Date(ts * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
}

export const SAMPLES_BASE = `${API_BASE}/samples`
